import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Link,
  Divider,
  Flex,
  CloseButton,
  Center,
  Button,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { FaArrowRight, FaArrowLeft } from 'react-icons/fa';
import { MdGavel } from 'react-icons/md';
import { ROSTER_SECTIONS_CONFIG, getActiveRosterSize, CURRENT_YEAR, ROSTER_MOVES_CONFIRMATION_MESSAGES, ROSTER_MOVES_CONFIRMATION_BUTTONS } from '../constants';
import { useUserContext } from '../contexts/UserContext';
import { useModernToast } from './ModernToast';
import QuickPurchaseModal, { QuickPurchaseResult } from './QuickPurchaseModal';
import { formatSalary } from '../utils/formatMoney';
import { useUserStore } from '../store/userStore';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useDeals } from '../hooks/useDeals';
import { formatPstDateTime, getPstTimeDifference } from '../lib/pstTime';
import { DEAL_STATUS_CONFIG } from '../constants';
import dayjs from 'dayjs';
import { useTeamRoster } from '../hooks/useTeamRoster';
import {
  usePlayerFullStats,
  getBattingStatRows,
  getPitchingStatRows,
  rowSeasonLabel,
  sortSimStatRows,
} from '../hooks/usePlayerFullStats';
import type { ShopItem } from '../store/shoppingCartStore';
import ConfirmDialog from './ConfirmDialog';
import { getTeamNameById, getTeamInfoById } from '../utils/getTeamInfo';
import { showPlayerNotes } from '../utils/showPlayerNotes';
import RecentDominusActivity from './RecentDominusActivity';
import { formatPositionsList } from '../utils/formatPositionsList';
import { httpsPlayerImageUrl } from '../utils/stringUtils';
import ILReplacementModal from './ILReplacementModal';


/**
 * Formats deal status display for PlayerCard
 * @param dealStatus - The deal status from the database
 * @param dealUntil - The deal until date
 * @returns Formatted string for display
 */
function formatDealStatusDisplay(dealStatus?: string, dealUntil?: string): string | null {
  if (!dealStatus) return null;

  // Skip free agent statuses - they're redundant with the main status display
  if (dealStatus.toLowerCase().includes('free')) {
    return null;
  }

  // Skip option_hold - it should be handled by the Option Hold Status Display section
  if (dealStatus === 'option_hold') {
    return null;
  }

  // For awaiting_assignment, only show the date (skip the status text since it's in the title)
  if (dealStatus.toLowerCase().includes('awaiting_assignment')) {
    if (dealUntil) {
      const timeDisplay = formatPstDateTime(dealUntil, true);
      return `until ${timeDisplay}`;
    }
    return null;
  }

  if (dealStatus === 'franchise_tag') {
    return `FRANCHISE TAG`;
  }

  // Special handling for qualifying_offer_made - don't show "until" date
  if (dealStatus === 'qualifying_offer_made') {
    return 'Qualifying offer made';
  }

  // Special handling for pending_fa_offer - show as "Pending free agent offer"
  if (dealStatus === 'pending_fa_offer') {
    const statusText = 'Pending free agent offer';
    if (dealUntil) {
      const timeDisplay = formatPstDateTime(dealUntil, true);
      return `${statusText} until ${timeDisplay}`;
    }
    return statusText;
  }

  const statusConfig = DEAL_STATUS_CONFIG[dealStatus as keyof typeof DEAL_STATUS_CONFIG];
  const isWaiverStatus = dealStatus === 'waivers' || dealStatus === 'waived' || dealStatus === 'claimed' || dealStatus === 'waivers_outright';
  let statusText: string;
  if (isWaiverStatus) {
    statusText = 'On waivers';
  } else if (statusConfig) {
    statusText = statusConfig.text;
  } else {
    // Convert unrecognized status to Sentence case
    // Replace underscores with spaces, then capitalize only the first letter
    const words = dealStatus.replace(/_/g, ' ').split(' ');
    statusText = words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase() +
      (words.length > 1 ? ' ' + words.slice(1).join(' ').toLowerCase() : '');
  }

  if (dealUntil) {
    const untilText = 'until';

    // Use the same PST formatting function as AvailablePlayersContent
    const timeDisplay = formatPstDateTime(dealUntil, true);

    return `${statusText} ${untilText} ${timeDisplay}`;
  }

  return statusText;
}

// Minimal RosterEntry type for this card
interface PlayerCardProps {
  player: {
    id: number;
    primaryPosition?: string[];
    secondaryPosition?: string[];
    tertiaryPosition?: string[];
    rwPlayer: {
      id: number;
      first_name: string;
      last_name: string;
      image?: string;
      position_1?: string;
      position_2?: string;
      position_3?: string;
      bats?: string;
      throws?: string;
      mls?: number;
      options?: string;
    };
    additionalInfo?: {
      mls?: number;
      options?: number;
      prior_or?: string;
      rule_five_eligibility?: boolean;
      days_active?: number[];
      team_started_season?: number;
    };
    playerContract?: {
      current_salary: number;
      contractYears?: { year: number; salary: number }[];
      options?: number; // Added for option years
    };
    contract?: {
      current_salary: number;
      contractYears?: { year: number; salary: number }[];
    };
    status?: string;
    status_until?: string | null;
    level?: string;
    is_40_man?: boolean; // Added for 40-man roster options
    team_id?: number; // Add team_id from roster entry
    options?: number; // Added for option years
    deal_status?: string; // Deal status (waivers, proposed, etc.)
    deal_until?: string; // Deal status until date
    dealData?: {
      claims?: number;
      bids?: number;
      dealId?: number | null;
      type?: string;
      expires?: string;
      teams?: number[];
      offers?: Array<{
        team: number;
        offer: any;
        created?: string;
        updated?: string;
      }>;
    };
  };
  team?: {
    color_primary?: string;
    color_secondary?: string;
  };
  subListLabel: string;
  onClose?: () => void;
  isMyTeam?: boolean; // Change to boolean prop
  refetch?: () => Promise<void>; // Add refetch prop for roster updates
  isFreeAgentView?: boolean;
  isWaiverWireView?: boolean;
  onTradeRequest?: (player: any) => void; // Callback for trade requests
  userAction?: (player: any, action: string, detail?: any) => void; // Generic callback for user actions
  hasFranchiseTag?: boolean; // Whether the team has already used their franchise tag
  rosterLocked?: boolean; // Whether the roster is currently locked
}

type ActionMenuFn = ((isRosterExpansion: boolean) => { label: string; note?: string }[]) | ((subList: string) => { label: string; note?: string }[]) | (() => { label: string; note?: string }[]);

const ACTION_MENUS: {
  [key: string]: ActionMenuFn;
  activeRoster: (isRosterExpansion: boolean) => { label: string; note?: string, prohibited?: boolean, message?: string }[];
  dl10: () => { label: string; note?: string }[];
  dl60: () => { label: string; note?: string }[];
  dl7: () => { label: string; note?: string }[];
  rehab: () => { label: string; note?: string }[];
  bereavement: () => { label: string; note?: string }[];
  paternity: () => { label: string; note?: string }[];
  restricted: () => { label: string; note?: string }[];
  suspended: () => { label: string; note?: string }[];
  dfa: () => { label: string; note?: string }[];
  waivers: () => { label: string; note?: string }[];
  minorLeague: (subList: string) => { label: string; note?: string }[];
  milbDL: () => { label: string; note?: string }[];
} = {
  activeRoster: (isRosterExpansion: boolean) => isRosterExpansion ? [
    { label: "Move to 10 Day IL" },
    // { label: "Move to 60 Day IL" },
    { label: "Outright to AAA" },
    { label: "Option to AAA", },
    { label: "Designate for Assignment and Waivers" },
    { label: "Release", note: "You’re still responsible for entire contract" },
  ] : [
    { label: "Option to AAA" },
    { label: "Outright Waivers" },
    { label: "Move to 10 Day IL", note: "You’ll need to replace with a minor leaguer" },
    { label: "Designate for Assignment and Waivers" },
    { label: "Release", note: "You’re still responsible for entire contract" },
    // { label: "Move to 60 Day IL" },
    // { label: "Move to Paternity List" },
    // { label: "Move to Bereavement List" },
    // { label: "Move to Restricted List" },
    // { label: "Move to Suspended List" },
  ],
  dl10: () => [
    { label: "Activate from Injured List" },
    { label: "Move to 60 Day IL" },
    { label: "Outright to AAA" },
    { label: "Option to AAA" },
    { label: "Designate for Assignment and Waivers", button1: 'Waive Him', button2: 'Waive' },
    { label: "Release", note: "You’re still responsible for entire contract" },
  ],
  dl60: () => [
    { label: "Promote to MLB", note: "Add to your 40 man roster" },
    { label: "Outright to AAA" },
    { label: "Option to AAA", note: "Returns to 40 man roster" },
    { label: "Designate for Assignment and Waivers", button1: 'Waive Him', button2: 'Waive' },
    { label: "Release", note: "You’re still responsible for entire contract" },
  ],
  dl7: () => [
    { label: "Option to AAA" },
    { label: "Move to Paternity List" },
    { label: "Move to Bereavement List" },
  ],
  rehab: () => [
    { label: "Option to AAA" },
    { label: "Move to Paternity List" },
    { label: "Move to Bereavement List" },
  ],
  bereavement: () => [
    { label: "Option to AAA" },
    { label: "Move to 10 Day IL" },
    { label: "Move to Paternity List" },
  ],
  paternity: () => [
    { label: "Option to AAA" },
    { label: "Move to 10 Day IL" },
    { label: "Move to Bereavement List" },
  ],
  restricted: () => [
    { label: "Outright to AAA" },
    { label: "Designate for Assignment and Waivers" },
    { label: "Release", note: "You’re still responsible for entire contract" },
  ],
  suspended: () => [
    { label: "Outright to AAA" },
    { label: "Designate for Assignment and Waivers" },
    { label: "Release", note: "You’re still responsible for entire contract" },
  ],
  awaitingAssignment: () => [
    { label: "Promote to MLB", note: "Add to your 40 man roster" },
    { label: "Option to AAA" },
  ],
  dfa: () => [
    { label: "Outright to AAA" },
    { label: "Release", note: "You’re still responsible for entire contract" },
  ],
  waivers: () => [],
  minorLeague: (subList: string, is40Man: boolean) => {
    const moves = [
      { label: "Designate for Assignment and Waivers", match: "dfa" },
      { label: "Move to AAA", match: "AAA", note: undefined },
      { label: "Move to AA", match: "AA", note: undefined },
      { label: "Move to High A", match: "High A", note: undefined },
      { label: "Move to Low A", match: "Low A", note: undefined },
      // { label: "Move to Advanced Rookie Ball", match: "Advanced Rookie Ball", note: undefined },
      { label: "Move to Rookie Ball", match: "Rookie Ball", note: undefined },
      // { label: "Move to EST", match: "EST", note: undefined },
      { label: "Move to DSL", match: "DSL", note: undefined },
      // { label: "Move to Minor League IL", match: "Minor League DL", note: undefined },
    ];
    return [
      { label: "Promote to MLB", note: "Add to your 40 man roster" },
      ...moves.filter(move => {
        // Exclude moves that match the current subList
        if (move.match === subList) return false;
        // Only include DFA if player IS on 40-man roster
        if (move.match === "dfa") return is40Man;

        return true;
      }),
      { label: "Release", note: undefined },
    ];
  },
  milbDL: () => [
    { label: "Outright to AAA" },
    { label: "Designate for Assignment and Waivers" },
    { label: "Release", note: "You’re still responsible for entire contract" },
  ],
};

const MENU_OPTION_TO_AAA = { label: "Option to AAA" };
const MENU_TO_DFA = { label: "Designate for Assignment and Waivers" };
const MENU_TO_WAIVERS = { label: "Put on Waivers" };
const MENU_MOVE_TO_60_DAY_IL = { label: "Move to 60 Day IL" };
const MENU_MOVE_TO_PATERNITY_LIST = { label: "Move to Paternity List" };
const MENU_MOVE_TO_BEREAVEMENT_LIST = { label: "Move to Bereavement List" };
const MENU_MOVE_TO_RESTRICTED_LIST = { label: "Move to Restricted List" };
const MENU_MOVE_TO_SUSPENDED_LIST = { label: "Move to Suspended List" };


const PlayerCard: React.FC<PlayerCardProps> = ({ player, team, subListLabel, onClose, isMyTeam = false, refetch, isFreeAgentView = false, isWaiverWireView = false, onTradeRequest, userAction, hasFranchiseTag, rosterLocked }) => {
  const rwP = player.rwPlayer;

  // Merge rwP with additionalInfo, where additionalInfo values override rwP values
  const playerInfo = {
    ...rwP,
    ...(player.additionalInfo && {
      mls: player.additionalInfo.mls ?? rwP.mls,
      options: player.additionalInfo.options ?? rwP.options,
      prior_or: player.additionalInfo.prior_or ?? (rwP as any).prior_or,
      rule_five_eligibility:
        player.additionalInfo.rule_five_eligibility ??
        (rwP as any).rule_five_eligibility,
      days_active:
        player.additionalInfo.days_active ?? (rwP as any).days_active,
      team_started_season:
        player.additionalInfo.team_started_season ??
        (rwP as any).team_started_season,
    }),
  };

  const positions = formatPositionsList(player);
  const borderColor = team?.color_primary || "#2b2d42";
  const { myTeam, systemSettings } = useUserContext();
  const { rosterMove, fortyManRosterSize, activeRosterSize } = useTeamRoster(myTeam || 0);
  const myLeague = useMyLeagueStore((state) => state.myLeague);
  const { getSalariesForLeague, salaries } = useDeals(myLeague?.id);
  const lastSalariesRequestLeagueId = useRef<number | null>(null);

  const otherTeamName = player.team_id ? getTeamNameById(player.team_id, myLeague) : '';
  const isPlayerOnOtherTeam = !isMyTeam && !!otherTeamName;

  useEffect(() => {
    const leagueId = myLeague?.id;
    if (!leagueId || !getSalariesForLeague || salaries) {
      return;
    }
    if (lastSalariesRequestLeagueId.current === leagueId) {
      return;
    }
    lastSalariesRequestLeagueId.current = leagueId;
    getSalariesForLeague().catch((err) => {
      console.error("Error fetching salaries:", err);
      lastSalariesRequestLeagueId.current = null;
    });
  }, [myLeague?.id, getSalariesForLeague, salaries]);

  // Trigger attention cue when card opens (on mount)
  useEffect(() => {
    // Disable attention after animation completes (~2600ms)
    const timer = setTimeout(() => {
      setAttentionActive(false);
    }, 2600);
    return () => clearTimeout(timer);
  }, []);

  // State for release confirmation dialog
  const [showConfirmation, setShowConfirmation] = useState(false);
  // State for waiver confirmation dialog
  const [confirmationMessages, setConfirmationMessages] = useState([]);
  const [confirmationButtons, setConfirmationButtons] = useState([]);
  const [transactionData, setTransactionData] = useState({});
  const [selectedMove, setSelectedMove] = useState("");
  const [invalidMove, setInvalidMove] = useState(false);
  // State for card flip
  const [isFlipped, setIsFlipped] = useState(false);
  const [isFranchisePurchaseOpen, setIsFranchisePurchaseOpen] = useState(false);
  // State for front-of-card arrow icon attention cue (one-time on open)
  const [attentionActive, setAttentionActive] = useState(true);
  // State for IL Replacement Modal
  const [showILReplacementModal, setShowILReplacementModal] = useState(false);
  const [isILProcessing, setIsILProcessing] = useState(false);

  const innerBorderColor = team?.color_secondary || "#ccc";
  const bgColor = "#21354a"; // much darker tan

  // Compute relative luminance to decide text contrast on team-colored banners
  const bannerTextColor = useMemo(() => {
    const hex = borderColor.replace('#', '');
    if (hex.length < 6) return 'white';
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const toLinear = (c: number) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const lum = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    return lum > 0.4 ? '#2b2d42' : 'white';
  }, [borderColor]);
  const bannerTextColorMuted = bannerTextColor === 'white' ? 'whiteAlpha.700' : 'rgba(43,45,66,0.6)';

  let section = ROSTER_SECTIONS_CONFIG.find(
    (sec) => sec.label === subListLabel
  );
  if (player.deal_status === "awaiting_assignment" || subListLabel === "Awaiting Assignment") {
    section = { actionMenu: "awaitingAssignment" };
  }
  const toast = useModernToast();
  // Removed unused leagueAffiliations
  const user = useUserStore((state) => state.user);
  const userRole =
    user && "role" in user && typeof user.role === "string" ? user.role : "";
  const isAdminOrSupport =
    userRole.toUpperCase() === "ADMIN" ||
    userRole.toUpperCase() === "SUPPORT" ||
    userRole.toUpperCase() === "EXEC";

  // validate roster move
  const validateRosterMove = (transactionName: string) => {
    // debugger;
    // if (transactionName === 'Option to AAA') {
    //  // put up confirmation dialog with notification that you cannot do this action
    //  setInvalidMove(true);
    //  setShowConfirmation(true);
    //  setConfirmationMessages([
    //   `You cannot option <firstName> <lastName> to AAA.`,
    //   `You must first designate him for assignment and waivers.`,
    //  ]);
    //  setConfirmationButtons([
    //    'Got it',
    //    ''
    //  ]);
    //  return false;
    // }
    // setInvalidMove(false);
    return true;
  };



  // Handle confirmation response
  const handleConfirmationResponse = async (result: "Yes" | "No") => {
    setShowConfirmation(false);

    if (result === "Yes" && !invalidMove) {
      try {
        const apiTransactionName = (transactionData as { transactionName?: string })?.transactionName ?? selectedMove;
        await rosterMove(player.id, apiTransactionName);
        if (refetch) await refetch();
        toast.success("Roster move completed", `${selectedMove} successful`);
      } catch (error) {
        console.error("Roster move failed:", error);
        toast.error("Roster move failed", "Please try again");
      }
    }

    // Reset states
    setSelectedMove("");
    setConfirmationMessages([]);
    setInvalidMove(false);
  };

  const rosterMoveName = (transactionName: string, player: any) => {
    const nameParts = transactionName.split(" ");
    // insert player's last name in 2nd spot in array
    nameParts.splice(1, 0, player.last_name);

    // if invalid, then add "Cannot" to the front of the array
    if (invalidMove) {
      nameParts.unshift("Cannot");
    }

    return nameParts.join(" ");
  };

  // show message on why a move is prohibited
  const handleRosterMoveProhibited = async (
    transactionName: string,
    message: string
  ) => {
    setInvalidMove(true);
    setSelectedMove(transactionName);
    setShowConfirmation(true);
    setConfirmationMessages([message]);
    setConfirmationButtons(["Got it", ""]);
  };

  // Handle IL replacement confirmation (two sequential roster moves)
  const handleILReplacementConfirm = async (replacementPlayer: any) => {
    setIsILProcessing(true);
    try {
      // First: Move player to 10 Day IL
      await rosterMove(player.id, "Move to 10 Day IL");
      // Second: Promote replacement to MLB
      await rosterMove(replacementPlayer.id, "Promote to MLB");
      if (refetch) await refetch();
      toast.success(
        "Roster moves completed",
        `${playerInfo.last_name} moved to 10 Day IL. ${replacementPlayer.rwPlayer.first_name} ${replacementPlayer.rwPlayer.last_name} added to active roster.`
      );
      setShowILReplacementModal(false);
      if (onClose) onClose();
    } catch (error) {
      console.error("IL replacement roster move failed:", error);
      toast.error("Roster move failed", "Please try again");
    } finally {
      setIsILProcessing(false);
    }
  };

  // Handle roster moves
  const handleRosterMove = async (transactionName: string) => {
    if (!validateRosterMove(transactionName)) {
      return;
    }

    // Intercept "Move to 10 Day IL" to open the IL Replacement Modal
    if (transactionName === "Move to 10 Day IL") {
      setShowILReplacementModal(true);
      return;
    }

    // Show confirmation dialog for Release action
    setSelectedMove(transactionName);
    const transactionData =
      actions.find((action) => action.label === transactionName) ||
      ({ label: transactionName } as any);
    setTransactionData(transactionData);

    const apiTransactionName = (transactionData as { transactionName?: string })?.transactionName || transactionName;
    setShowConfirmation(true);
    let messages = ROSTER_MOVES_CONFIRMATION_MESSAGES[apiTransactionName] || [];
    if (apiTransactionName === "Designate for Assignment and Waivers") {
      let priorOR = player.additionalInfo?.prior_or || player.rwPlayer.prior_or;
      let mls = player.additionalInfo?.mls || player.rwPlayer.mls;
      // debugger;
      if (priorOR !== "YES" && mls < 5) {
        messages = ROSTER_MOVES_CONFIRMATION_MESSAGES["Designate for Assignment and Waivers - To AAA"] || [];
      }
    }
    const fullMessages: string[] = [];
    messages.forEach((message) => {
      message = message.replace("<firstName>", playerInfo.first_name);
      message = message.replace("<lastName>", playerInfo.last_name);
      if (apiTransactionName === "Extend Offer") {
        message = message.replace(
          "<salary>",
          formatSalary(salaries?.qualifyingOfferSalary, true)
        );
      }
      fullMessages.push(message);
    });
    setConfirmationMessages(fullMessages);
    setConfirmationButtons([
      ROSTER_MOVES_CONFIRMATION_BUTTONS[apiTransactionName]?.button1
        ? ROSTER_MOVES_CONFIRMATION_BUTTONS[apiTransactionName].button1
        : transactionName,
      ROSTER_MOVES_CONFIRMATION_BUTTONS[apiTransactionName]?.button2
        ? ROSTER_MOVES_CONFIRMATION_BUTTONS[apiTransactionName].button2
        : "Cancel",
    ]);
    return;
  };

  // Check if deal status includes settlement or review - if so, hide actions
  const isDealInSettlementOrReview =
    player.deal_status &&
    (player.deal_status.toLowerCase().includes("settlement") ||
      player.deal_status.toLowerCase().includes("review") ||
      player.deal_status.toLowerCase().includes("claimed") ||
      player.deal_status.toLowerCase().includes("free_agent") ||
      player.deal_status.toLowerCase().includes("trade") ||
      player.deal_status.toLowerCase().includes("waived"));
  let actions: { label: string; note?: string; transactionName?: string }[] = [];
  const isFreeAgent =
    player.deal_status && player.deal_status.toLowerCase().includes("free");

  // Check if player is waived (any waiver-related status)
  const isWaived =
    player.deal_status &&
    (player.deal_status.toLowerCase().includes("waived") ||
      player.deal_status.toLowerCase().includes("waivers") ||
      player.deal_status.toLowerCase().includes("claimed") ||
      player.deal_status.toLowerCase().includes("settlement_waiver_claim"));

  // Check if player is in settlement
  const isSettlementTrade = player.deal_status === 'settlement_trade';
  const isSettling =
    player.deal_status?.toLowerCase().includes("settlement") || false;

  // check if this player is a franchise tag player
  const isFranchiseTag =
    player.playerContract?.contractYears?.[0]?.contract_type ===
    "Franchise Tag";

  // check if this player has a contract extension
  const isContractExtension =
    player.playerContract?.contractYears?.[0]?.contract_type ===
    "Contract Extension";

  // Check if trades are allowed based on system settings
  const isTradeAllowed = () => {
    if (!systemSettings) return true; // Default to allowing trades if no settings

    const seasonStatus = systemSettings.seasonStatus;
    const tradeDeadline = systemSettings.tradeDeadline;

    // Trades allowed if seasonStatus === "Off Season"
    if (seasonStatus === "Off Season") {
      return true;
    }

    // Trades NOT allowed if seasonStatus === "In Season" and today's date is after the tradeDeadline
    if (seasonStatus === "In Season" && tradeDeadline) {
      const today = dayjs();
      const deadline = dayjs(tradeDeadline);
      return today.isBefore(deadline);
    }

    // Default to allowing trades if conditions don't match
    return true;
  };

  // Check if player has deal status that prevents all actions
  const dealStatus = player.deal_status?.toLowerCase() || "";
  // During "Off Season Quiet", franchise tag players should have normal actions
  const isOffSeasonQuiet = systemSettings?.seasonStatus === 'Off Season Quiet';
  const hasRestrictedDealStatus =
    dealStatus.includes("waive") ||
    dealStatus.includes("claim") ||
    (isFranchiseTag && !isOffSeasonQuiet) ||
    player.deal_status === "trade";

  // Check if rosters are frozen
  const isRosterFrozen = myLeague?.roster_editing === "Frozen";

  // now set actions for the player card
  if (isWaiverWireView) {
    if (hasRestrictedDealStatus) {
      actions = [];
    } else {
      actions = [{ label: "Claim Waiver" }]; // Keep Claim Waiver even when frozen
    }
  } else if (isFreeAgentView) {
    actions = []; // No action links - using button instead
  } else if (isMyTeam) {
    if (hasRestrictedDealStatus) {
      actions = [];
    } else if (isFreeAgent) {
      actions = [{ label: "Sign to Majors" }];
    } else if (section && !hasRestrictedDealStatus) {
      if (section.actionMenu === "activeRoster") {
        actions = ACTION_MENUS.activeRoster(getActiveRosterSize() === 28);
      } else if (section.actionMenu === "minorLeague") {
        actions = ACTION_MENUS.minorLeague(subListLabel, player.is_40_man);
      } else if (typeof ACTION_MENUS[section.actionMenu] === "function") {
        actions = (
          ACTION_MENUS[section.actionMenu] as () => {
            label: string;
            note?: string;
          }[]
        )();
      }
    }
  } else {
    if (hasRestrictedDealStatus) {
      actions = [];
    } else if (!isFreeAgent) {
      actions = [{ label: "Offer a Trade" }];
    } else {
      actions = [];
    }
  }

  // Clear actions if rosters are frozen or locked (except Claim Waiver which is handled separately)
  if ((isRosterFrozen || rosterLocked) && !isWaiverWireView) {
    actions = [];
  }

  // ************check for prohibited moves
  if (
    playerInfo.mls >= 5 ||
    !playerInfo.options ||
    parseInt(playerInfo.options) === 0
  ) {
    const optionToAAA = actions.find(
      (action) => action.label === "Option to AAA"
    );
    if (optionToAAA) {
      optionToAAA.prohibited = true;
      const hasMLS = playerInfo?.mls >= 5;
      const noOptions =
        !playerInfo?.options || parseInt(playerInfo?.options) === 0;
      optionToAAA.message = `You cannot option this player because he has 
              ${hasMLS ? "more than 5 years of Major League Service" : ""} 
              ${hasMLS && noOptions ? "and" : ""} 
              ${noOptions ? "has no options" : ""} .`;
    }
  }
  if (playerInfo?.prior_or === "YES") {
    const priorOR = actions.find(
      (action) => action.label === "Outright to AAA"
    );
    if (priorOR) {
      priorOR.prohibited = true;
      priorOR.message = `You cannot option this player because he has a Prior OR.`;
    }
  }
  const is10DayIL =
    player.status === "DL10" ||
    player.status?.toLowerCase() === "dl10" ||
    player.status === "10 day dl" ||
    player.status === "10-day dl" ||
    player.status === "10 day il" ||
    player.status === "10-day il";
  if (is10DayIL || player.status === "Option Hold") {
    // check date of DL10 and if it's less than 10 days, prohibit the move (skip when status_until is null)
    const statusUntilPassed =
      player.status_until == null ||
      getPstTimeDifference(player.status_until) <= 0;
    if (!statusUntilPassed) {
      const dl10 = actions.find(
        (action) =>
          action.label === "Promote to MLB" ||
          action.label === "Activate from Injured List"
      );
      if (dl10) {
        dl10.prohibited = true;
        const submessage =
          player.status === "Option Hold"
            ? "is on an option hold"
            : "hasn't been on the DL10 list";
        dl10.message = `You cannot promote this player because he ${submessage} for at least 10 days. You can promote him after ${formatPstDateTime(
          player.status_until,
          false
        )}.`;
      }
    } else if (section?.actionMenu === "dl10" && is10DayIL) {
      // 10 day IL and status_until has passed or is null → show "Activate from Injured List"
      const promoteAction = actions.find((action) => action.label === "Promote to MLB");
      if (promoteAction) {
        promoteAction.label = "Activate from Injured List";
        promoteAction.transactionName = "Activate from Injured List";
      }
    }
  }

  if (player.deal_status === "option_hold") {
    const optionHold = actions.find((action) => action.label === "Promote to MLB");
    if (optionHold) {
      optionHold.prohibited = true;
      optionHold.message = `You cannot promote this player because he is on an option hold.`;
    }
  }

  // Awaiting Assignment: prohibit "Promote to MLB" when 40-man roster is full
  let rosterLimit = 40;
  if (player.is_40_man) {
    rosterLimit = 41;
  }
  if (section?.actionMenu === "awaitingAssignment" && (fortyManRosterSize ?? 0) >= rosterLimit) {
    const promoteToMLB = actions.find((action) => action.label === "Promote to MLB");
    if (promoteToMLB) {
      promoteToMLB.prohibited = true;
      promoteToMLB.message =
        "You currently already have 40 players on your 40 man roster. You'll need to make room on your roster before you can promote this player.";
    }
  }

  // Active roster full: prohibit "Promote to MLB" / "Activate from Injured List" (same "Why?" pattern as other restricted moves)
  const activeRosterLimit = getActiveRosterSize();
  if (
    activeRosterSize != null &&
    activeRosterSize >= activeRosterLimit
  ) {
    const activeRosterFullMessage = `You already have ${activeRosterSize} players on your active roster (maximum ${activeRosterLimit} for the current period). Open a spot on your active roster before you can promote or activate this player.`;
    for (const action of actions) {
      if (
        (action.label === "Promote to MLB" ||
          action.label === "Activate from Injured List") &&
        !action.prohibited
      ) {
        action.prohibited = true;
        action.message = activeRosterFullMessage;
      }
    }
  }

  // Handler for trade button
  const handleTradeClick = () => {
    if (!myTeam || !player.team_id) {
      toast.error("Error", "Missing team information for trade");
      return;
    }
    // Call the onTradeRequest callback if available, otherwise fall back to userAction
    if (onTradeRequest) {
      onTradeRequest(player);
    } else if (userAction) {
      userAction(player, "trade");
    }
  };

  // Determine if the free-agent offer window is still open for this player
  const isFreeAgentOfferWindowOpen = (() => {
    const expires = player.dealData?.expires;
    if (!expires) return true;
    const diffMinutes = getPstTimeDifference(expires);
    return diffMinutes > 0;
  })();

  // handler for free agent offer button
  const handleMakeOfferClick = () => {
    const seasonStatus = systemSettings?.seasonStatus || null;
    // If the offer window has closed, match the list text and block the action
    if (!isFreeAgentOfferWindowOpen) {
      setSelectedMove("Make Free Agent Offer");
      setInvalidMove(true);
      setShowConfirmation(true);
      setConfirmationMessages([
        "This player is no longer accepting offers.",
      ]);
      setConfirmationButtons(["Got it", ""]);
      return;
    }

    if (seasonStatus && (seasonStatus === "Off Season" || seasonStatus === "In Season")) {
      userAction?.(player, "make_offer");
      return;
    }

    // In the part of the off season (Off Season EFQO or Off Season Quiet) when users cannot make offers, show the existing confirmation dialog
    setSelectedMove("Make Free Agent Offer");
    setInvalidMove(true);
    setShowConfirmation(true);
    setConfirmationMessages([
      "You cannot make an offer at this time. Offers will be accepted after the first of the year",
    ]);
    setConfirmationButtons(["Got it", ""]);
  };

  const isQualifiedForOfferContext = player.deal_status === "efqo"; // can be offered qualifying offer.
  const isPendingFreeAgencyContext = player.deal_status === "pending_fa_offer"; // can be offered franchise tag

  const parseCurrency = (value?: string | number | null) => {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === "string") {
      const numeric = Number(value.replace(/[^0-9.-]/g, ""));
      return Number.isFinite(numeric) ? numeric : undefined;
    }
    return undefined;
  };

  const franchiseTagPrice = useMemo(() => {
    const leagueValue = parseCurrency(myLeague?.qualifying_offer_salary);
    const salaryValue = parseCurrency(
      salaries?.franchiseTagPrice ?? salaries?.qualifyingOfferSalary
    );
    const settings = systemSettings || {};
    const settingsSource =
      settings["franchise_tag_price"] ??
      settings["franchiseTagPrice"] ??
      settings["franchiseTagCost"] ??
      settings["franchise_tag_cost"] ??
      settings["defaultQualifiedOffer"];
    const settingsValue = parseCurrency(settingsSource);
    return leagueValue ?? salaryValue ?? settingsValue ?? 0;
  }, [
    myLeague?.qualifying_offer_salary,
    salaries?.franchiseTagPrice,
    salaries?.qualifyingOfferSalary,
    systemSettings,
  ]);

  const franchiseTagItem = useMemo<ShopItem | null>(() => {
    if (!player || !player.rwPlayer) {
      return null;
    }

    if (!franchiseTagPrice || Number.isNaN(franchiseTagPrice)) {
      return null;
    }

    return {
      id: `franchise-tag-${player.id}`,
      name: "Franchise Tag",
      description: `Purchase a one-year contract extension for ${player.rwPlayer.first_name} ${player.rwPlayer.last_name}.`,
      price: Math.round(franchiseTagPrice),
      currency: "USD",
      category: "features",
      metadata: {
        playerId: player.id,
        leagueId: myLeague?.id,
        teamId: myTeam ?? undefined,
        transaction: "franchise_tag",
        playerPosition: player.primaryPosition?.[0] ?? player.rwPlayer.position_1 ?? '',
      },
    };
  }, [player, franchiseTagPrice, myLeague?.id, myTeam]);

  const shouldShowOfferButton =
    !isSettling &&
    isMyTeam &&
    (isQualifiedForOfferContext || isPendingFreeAgencyContext) &&
    player.team_id === myTeam;

  const isPendingFA = player.deal_status === 'pending_fa_offer';
  const offerButtonText = isPendingFA ? 'Buy Franchise Tag' : 'Extend Qualifying Offer';
  const franchiseDisabled = isPendingFA && (!franchiseTagPrice || Number(franchiseTagPrice) <= 0);
  // Disable Buy Franchise Tag button if hasFranchiseTag is not false
  const franchiseTagUnavailable = isPendingFA && hasFranchiseTag !== false;
  const offerButtonDisabled = player.deal_status === 'qa_offered' || franchiseDisabled || franchiseTagUnavailable;


  const handleFranchiseTagPurchaseCreated = (result: QuickPurchaseResult) => {
    if (result.checkoutUrl) {
      window.open(result.checkoutUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const leagueSeasonYear =
    typeof myLeague?.year === 'number' && Number.isFinite(myLeague.year)
      ? myLeague.year
      : undefined;

  const fullStatsQuery = usePlayerFullStats({
    leagueId: myLeague?.id,
    rosterPlayerId: player.id,
    season: leagueSeasonYear,
    enabled: Boolean(isFlipped && myLeague?.id && player.id),
  });

  const formatAvg3 = (v: unknown) => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(3) : '—';
  };

  /** Sim pitching `ip` is baseball notation (e.g. "62.200"), not a decimal. */
  const formatIpDisplay = (v: unknown) => {
    if (v == null || v === '') return '—';
    return String(v).trim();
  };

  const formatDecimalFixed = (v: unknown, places: number) => {
    if (v == null || v === '') return '—';
    const n = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n.toFixed(places) : '—';
  };
  return (
    <>
      <Box
        p={0}
        border="12px solid transparent"
        borderRadius="32px"
        boxShadow="lg"
        bg="transparent"
        position="relative"
        width="450px"
        height="710px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{ perspective: "1000px" }}
      >
        <Box
          position="relative"
          width="100%"
          height="100%"
          style={{
            transformStyle: "preserve-3d",
            transition: "transform 0.6s",
            transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          {/* Front Side — Baseball Card Style */}
          <Box
            p={0}
            border={`5px solid ${innerBorderColor}`}
            borderRadius="20px"
            bg="#f5f0e1"
            width="100%"
            height="100%"
            position="absolute"
            top={0}
            left={0}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            pointerEvents={isFlipped ? "none" : "auto"}
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
            }}
          >
            {/* Outer team-color border */}
            <Box
              width="100%"
              height="100%"
              border={`4px solid ${borderColor}`}
              borderRadius="16px"
              position="absolute"
              top={0}
              left={0}
              pointerEvents="none"
              zIndex={0}
            />
            {onClose && (
              <CloseButton
                position="absolute"
                top={2}
                right={2}
                zIndex={3}
                color={bannerTextColor}
                onClick={onClose}
              />
            )}

            {/* ── Header Banner — team color ── */}
            <Box
              bg={borderColor}
              px={4}
              py={2}
              borderTopRadius="12px"
              position="relative"
              overflow="hidden"
              flexShrink={0}
            >
              {/* Diagonal accent stripe */}
              <Box
                position="absolute"
                top={0}
                right={0}
                width="140px"
                height="100%"
                bg={innerBorderColor}
                opacity={0.18}
                style={{ clipPath: "polygon(30% 0, 100% 0, 100% 100%, 0% 100%)" }}
              />
              <Text
                fontSize="xl"
                fontWeight="800"
                color={bannerTextColor}
                letterSpacing="0.5px"
                textTransform="uppercase"
                lineHeight="1.2"
                pr={8}
              >
                {playerInfo.first_name} {playerInfo.last_name}
                {positions && (
                  <Text
                    as="span"
                    fontSize="xs"
                    fontWeight="600"
                    color={bannerTextColorMuted}
                    letterSpacing="1.5px"
                    ml={2}
                  >
                    — {positions}
                  </Text>
                )}
              </Text>
              {isPlayerOnOtherTeam && (
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  color={bannerTextColorMuted}
                  letterSpacing="1px"
                  textTransform="uppercase"
                  mt="2px"
                >
                  {otherTeamName}
                </Text>
              )}
            </Box>

            {/* ── Photo + Info Section ── */}
            <Flex
              bg="#e8e0cc"
              px={4}
              py={3}
              gap={4}
              align="flex-start"
              flexShrink={0}
              borderBottom="2px solid #d4cbb0"
            >
              {/* Player photo — card-style rectangle */}
              {httpsPlayerImageUrl(playerInfo.image) ? (
                <Box
                  position="relative"
                  width="100px"
                  height="120px"
                  borderRadius="4px"
                  overflow="hidden"
                  border="2px solid #d4cbb0"
                  bg="gray.600"
                  flexShrink={0}
                  boxShadow="0 1px 3px rgba(0,0,0,0.15)"
                >
                  <img
                    src={httpsPlayerImageUrl(playerInfo.image)}
                    alt={`${playerInfo.first_name} ${playerInfo.last_name}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </Box>
              ) : (
                <Center
                  width="100px"
                  height="120px"
                  borderRadius="4px"
                  border="2px solid #d4cbb0"
                  bg={
                    getTeamInfoById(player.team_id, myLeague)?.color_primary ||
                    "gray.600"
                  }
                  flexShrink={0}
                  boxShadow="0 1px 3px rgba(0,0,0,0.15)"
                >
                  <Text
                    fontSize="2xl"
                    fontWeight="bold"
                    color={
                      getTeamInfoById(player.team_id, myLeague)?.color_text ||
                      "white"
                    }
                  >
                    {playerInfo.first_name?.[0]}{playerInfo.last_name?.[0]}
                  </Text>
                </Center>
              )}

              {/* Info box — like the Topps info overlay */}
              <VStack align="flex-start" spacing={1} flex={1}>
                <Text fontSize="xs" color="#5a5040" fontWeight="700" letterSpacing="0.3px">
                  SAL: {formatSalary(
                    player.playerContract?.current_salary ||
                    player.contract?.current_salary,
                    true
                  )}
                </Text>
                {playerInfo.bats && (
                  <Text fontSize="xs" color="#5a5040" fontWeight="600">
                    BATS: {playerInfo.bats}
                    {playerInfo.throws ? `  THROWS: ${playerInfo.throws}` : ""}
                  </Text>
                )}
                {playerInfo.mls !== undefined && playerInfo.mls !== null && !isNaN(playerInfo.mls) && (
                  <Text fontSize="xs" color="#5a5040" fontWeight="600">
                    MLS: {playerInfo.mls.toFixed(3)}
                  </Text>
                )}
                {playerInfo.options !== undefined && playerInfo.options !== null && playerInfo.options !== "" && !isNaN(Number(playerInfo.options)) && (
                  <Text fontSize="xs" color="#5a5040" fontWeight="600">
                    OPT: {playerInfo.options}
                    {(() => {
                      const optionsRemaining = (player as any).options_remaining;
                      if (optionsRemaining != null && optionsRemaining !== "" && !isNaN(Number(optionsRemaining)) && Number(optionsRemaining) >= 0) {
                        return `  REM: ${optionsRemaining}`;
                      }
                      return "";
                    })()}
                  </Text>
                )}
                {/* Dominus branding — like the Topps logo */}
                <Text
                  fontSize="9px"
                  color="#b0a58a"
                  letterSpacing="2px"
                  textTransform="uppercase"
                  fontWeight="700"
                  mt="auto"
                  pt={1}
                >
                  Dominus Baseball
                </Text>
              </VStack>
            </Flex>

            {/* ── Status Strip ── */}
            <Box
              bg={borderColor}
              px={4}
              py={1}
              flexShrink={0}
            >
              {subListLabel === "Non-roster Players with Carried Salaries" ? (
                <VStack spacing={0} align="center">
                  <Text fontSize="xs" color={bannerTextColor} fontWeight="600">
                    Non-roster — Carried Salary
                  </Text>
                  <Text fontSize="xs" color={bannerTextColorMuted}>
                    {getTeamNameById(player.team_id, myLeague) || `Team ${player.team_id}`}
                    {" · "}
                    {(() => {
                      const carriedContract = (player as any).DominusContracts?.find(
                        (contract: any) => contract.carried === true
                      );
                      const carriedSalary = carriedContract?.current_salary || 0;
                      return formatSalary(carriedSalary, false);
                    })()}
                  </Text>
                </VStack>
              ) : (
                <Flex justify="center" align="center" gap={2}>
                  <Text
                    fontSize="xs"
                    color={bannerTextColor}
                    fontWeight="700"
                    letterSpacing="1px"
                    textTransform="uppercase"
                  >
                    {isFreeAgentView
                      ? "Free Agent"
                      : isWaived
                        ? `Waived by ${getTeamNameById(player.team_id, myLeague) || `Team ${player.team_id}`}`
                        : subListLabel}
                  </Text>
                  {!isFreeAgentView &&
                    player.is_40_man &&
                    player.status !== "Active" && (
                      <Text
                        fontSize="10px"
                        color={bannerTextColorMuted}
                        fontStyle="italic"
                      >
                        (40-Man)
                      </Text>
                    )}
                </Flex>
              )}
            </Box>

            {/* ── Actions / Content Area (scrollable) ── */}
            <Box
              flex={1}
              overflowY="auto"
              px={4}
              py={3}
              position="relative"
              sx={{
                WebkitOverflowScrolling: "touch",
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": { bg: "rgba(0,0,0,0.15)", borderRadius: "4px" },
              }}
            >

              {/* Settlement Information */}
              {isSettling &&
                player.dealData?.teams &&
                player.dealData.teams.length > 0 && (
                  <Box
                    bg="green.700"
                    p={3}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="green.600"
                    mb={4}
                  >
                    <Text fontSize="md" color="green.100" fontWeight="bold">
                      {(() => {
                        if (player.deal_status === "settlement_free_agent_offer") {
                          const signingTeam =
                            getTeamNameById(player.dealData.teams[0], myLeague) ||
                            `Team ${player.dealData.teams[0]}`;
                          return `Signed by ${signingTeam}`;
                        }
                        if (isSettlementTrade) {
                          const targetTeamId =
                            player.dealData.teams.find(
                              (teamId: number) =>
                                Number(teamId) !== Number(player.team_id)
                            ) ?? player.dealData.teams[0];
                          const targetTeam =
                            getTeamNameById(targetTeamId, myLeague) ||
                            `Team ${targetTeamId}`;
                          return `Traded to ${targetTeam}`;
                        }
                        const claimingTeam =
                          getTeamNameById(player.dealData.teams[0], myLeague) ||
                          `Team ${player.dealData.teams[0]}`;
                        return `Claimed by ${claimingTeam}`;
                      })()}
                    </Text>
                    <Text fontSize="sm" color="green.200" mt={1}>
                      Settlement closes at midnight
                    </Text>
                  </Box>
                )}
              {/* Contract/Options section for non-my-team or free agent */}
              {!isSettling && (!isMyTeam || isFreeAgentView) && (
                <Box mb={4}>
                  <Text fontWeight="bold" fontSize="sm" mb={1} color="#5a5040" letterSpacing="1px" textTransform="uppercase">
                    Contract
                  </Text>
                  <Flex mb={2} gap={8} flexWrap="wrap">
                    <Box>
                      <Text fontSize="sm" color="#8b7355">
                        Current Salary
                      </Text>
                      <Text fontSize="md" pl={4} color="#333">
                        {formatSalary(
                          player.playerContract?.current_salary ||
                          player.contract?.current_salary,
                          true
                        )}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" color="#8b7355">
                        Remaining Contract
                      </Text>
                      <Text fontSize="md" pl={4} color="#333">
                        {(() => {
                          const years =
                            player.playerContract &&
                              Array.isArray(
                                (player.playerContract as any).contractYears
                              )
                              ? (player.playerContract as any).contractYears
                              : [];
                          const total = years
                            .filter((y: any) => y.year >= CURRENT_YEAR)
                            .reduce(
                              (sum: number, y: any) =>
                                sum + (Number(y.salary) || 0),
                              0
                            );
                          return total > 0 ? formatSalary(total, true) : "-";
                        })()}
                      </Text>
                    </Box>
                  </Flex>
                  {/* <Text fontWeight="bold" fontSize="lg" mb={1}>
                  Options
                </Text>
                <Flex gap={8} flexWrap="wrap">
                  <Box>
                    <Text fontSize="sm" color="gray.400">
                      Option Years
                    </Text>
                    <Text fontSize="md" pl={4}>
                      {(() => {
                        const optionsValue = playerInfo.options;
                        if (optionsValue == null || optionsValue === "") return "-";
                        const optionsNum = typeof optionsValue === "number" ? optionsValue : parseInt(String(optionsValue));
                        return !isNaN(optionsNum) && optionsNum > 0 ? optionsNum : "-";
                      })()}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.400">
                      Remaining Options
                    </Text>
                    <Text fontSize="md" pl={4}>
                      {(() => {
                        const optionsRemaining = (player as any).options_remaining;
                        if (optionsRemaining == null || optionsRemaining === "") return "-";
                        const optionsRemainingNum = typeof optionsRemaining === "number" ? optionsRemaining : parseInt(String(optionsRemaining));
                        return !isNaN(optionsRemainingNum) && optionsRemainingNum >= 0 ? optionsRemainingNum : "-";
                      })()}
                    </Text>
                  </Box>
                </Flex> */}
                </Box>
              )}

              {/* Franchise Tag Display */}
              {isFranchiseTag &&
                (() => {
                  const notes = showPlayerNotes(player);
                  const franchiseTagNote = notes.length > 0 ? notes[0] : null;

                  return (
                    <Box m={4}>
                      <Center>{franchiseTagNote}</Center>
                    </Box>
                  );
                })()}

              {/* Contract Extension Display */}
              {isContractExtension &&
                (() => {
                  const notes = showPlayerNotes(player);
                  const extendedContractNote = notes.length > 0 ? notes[0] : null;

                  return (
                    <Box m={4}>
                      <Center>{extendedContractNote}</Center>
                    </Box>
                  );
                })()}

              {/* Deal Status Display */}
              {!isSettling && player.deal_status && (
                <Box mb={4}>
                  <Text fontSize="md" color="#5a5040" textAlign="center">
                    {player.deal_status === "qa_offered"
                      ? `Offer extended until ${formatPstDateTime(
                        player.deal_until || ""
                      )}`
                      : player.deal_status === "free_agent_offer"
                        ? (() => {
                          const expires = player.dealData?.expires;
                          if (!expires) {
                            return "Accepting offers";
                          }
                          const diffMinutes = getPstTimeDifference(expires);
                          const timeText = formatPstDateTime(expires, true);
                          if (diffMinutes > 0) {
                            return `Accepting offers until ${timeText}`;
                          }
                          return (
                            <>
                              No longer accepting offers
                              <br />
                              as of {timeText}
                            </>
                          );
                        })()
                        : formatDealStatusDisplay(
                          player.deal_status,
                          player.deal_until
                        )}
                  </Text>

                  {/* Claims Information for Waiver Wire */}
                  {isWaiverWireView &&
                    player.dealData &&
                    player.dealData.claims &&
                    player.dealData.claims > 0 && (
                      <Box
                        bg="#ece5d3"
                        p={3}
                        borderRadius="md"
                        border="1px solid #c4b89a"
                        mt={3}
                      >
                        <HStack align="center" spacing={2}>
                          <Box as={MdGavel} color="#5a4a2e" boxSize="14px" flexShrink={0} />
                          <Text fontSize="sm" color="#5a4a2e" fontWeight="700" letterSpacing="0.3px">
                            There {player.dealData.claims === 1 ? "is" : "are"}{" "}
                            {player.dealData.claims} claim
                            {player.dealData.claims === 1 ? "" : "s"} for{" "}
                            {playerInfo.last_name}.
                          </Text>
                        </HStack>
                      </Box>
                    )}

                  {/* Your team claim message */}
                  {player.deal_status === "claimed" &&
                    myTeam &&
                    player.dealData?.teams &&
                    player.dealData.teams.some(
                      (teamId: any) => Number(teamId) === Number(myTeam)
                    ) && (
                      <Box
                        bg="#d4cbb0"
                        p={3}
                        borderRadius="md"
                        border="1px solid #b0a584"
                        mt={3}
                      >
                        <Text fontSize="sm" color="#3d6b2e" fontWeight="800" letterSpacing="0.3px">
                          ✓ Your team put in a claim
                        </Text>
                      </Box>
                    )}

                  {/* Offers Information for Free Agent Offers */}
                  {isFreeAgentView &&
                    player.dealData &&
                    player.dealData.bids != null &&
                    player.dealData.bids > 0 && (
                      <Box
                        bg="#ece5d3"
                        p={3}
                        borderRadius="md"
                        border="1px solid #c4b89a"
                        mt={3}
                      >
                        <HStack align="center" spacing={2}>
                          <Box as={MdGavel} color="#5a4a2e" boxSize="14px" flexShrink={0} />
                          <Text fontSize="sm" color="#5a4a2e" fontWeight="700" letterSpacing="0.3px">
                            There {player.dealData.bids === 1 ? "is" : "are"}{" "}
                            {player.dealData.bids} offer
                            {player.dealData.bids === 1 ? "" : "s"} for{" "}
                            {playerInfo.last_name}.
                          </Text>
                        </HStack>


                        {player.dealData.offers &&
                          player.dealData.offers.some(
                            (offer: any) => offer.team === myTeam
                          ) &&
                          (() => {
                            const myTeamOffer = player.dealData.offers.find(
                              (offer: any) => offer.team === myTeam
                            );
                            const offerDate = myTeamOffer?.created
                              ? formatPstDateTime(myTeamOffer.created)
                              : "unknown date";

                            // Determine if this offer is currently the high bid
                            let isWinning = false;
                            if (player.dealData?.offers && player.dealData.offers.length > 0) {
                              const highestOfferScore = player.dealData.offers.reduce(
                                (max: number, offer: any) =>
                                  Math.max(max, offer.offer?.offerScore || 0),
                                0
                              );
                              if (
                                myTeamOffer &&
                                myTeamOffer.offer?.offerScore &&
                                Math.abs(myTeamOffer.offer.offerScore - highestOfferScore) < 0.001 &&
                                highestOfferScore > 0
                              ) {
                                isWinning = true;
                              }
                            }

                            return (
                              <Box mt={2} textAlign="center">
                                <Text
                                  fontSize="sm"
                                  color="#3d6b2e"
                                  fontWeight="bold"
                                >
                                  Your team submitted an offer on {offerDate}
                                </Text>
                                {isWinning && user?.systemSettings?.seasonStatus !== "In Season" && (
                                  <Text
                                    fontSize="sm"
                                    color="#8b6914"
                                    fontWeight="bold"
                                    mt={1}
                                  >
                                    Current High Bidder
                                  </Text>
                                )}
                              </Box>
                            );
                          })()}
                      </Box>
                    )}
                </Box>
              )}

              {/* Option Hold Status Display */}
              {(player.status === "Option Hold" ||
                player.deal_status === "option_hold") &&
                !(
                  player.deal_status &&
                  player.deal_status.toLowerCase().includes("free")
                ) && (
                  <Box mt="-8px" mb={1}>
                    <Center>
                      <Text fontSize="md" color="#5a5040">
                        {player.deal_until
                          ? `Available for recall ${formatPstDateTime(
                            player.deal_until
                          )}`
                          : player.status_until
                            ? `Available for recall ${formatPstDateTime(
                              player.status_until
                            )}`
                            : "Available for recall"}
                      </Text>
                    </Center>
                  </Box>
                )}

              {/* Injured List Status Display */}
              {(() => {
                const isInjuredList =
                  player.status === "DL10" ||
                  player.status === "10-day dl" ||
                  player.status === "10 day dl" ||
                  player.status === "10-day il" ||
                  player.status === "10 day il" ||
                  player.status === "DL60" ||
                  player.status === "60-day dl" ||
                  player.status === "60 day dl" ||
                  player.status === "60-day il" ||
                  player.status === "60 day il";

                // Don't show injured list status for free agent players
                if (
                  !isInjuredList ||
                  (player.deal_status &&
                    player.deal_status.toLowerCase().includes("free"))
                )
                  return null;

                const getStatusText = () => {
                  if (!player.status_until) return "Available for recall";
                  const diffMinutes = getPstTimeDifference(player.status_until);
                  return diffMinutes <= 0
                    ? "Available for recall"
                    : `Until at least ${formatPstDateTime(
                      player.status_until,
                      true
                    )}`;
                };

                return (
                  <Box mt={"-8px"} mb={1}>
                    <Center>
                      <Text fontSize="md" color="#5a5040">
                        {getStatusText()}
                      </Text>
                    </Center>
                  </Box>
                );
              })()}
              {/* Show frozen panel if rosters are frozen (except for Claim Waiver which is always available) */}
              {isRosterFrozen && !isWaiverWireView && (
                <>
                  <Divider my={4} borderColor="#d4cbb0" />
                  <Box
                    bg="#d4cbb0"
                    borderRadius="md"
                    p={4}
                    textAlign="center"
                  >
                    <Text fontSize="md" color="#5a5040" fontWeight="semibold">
                      Rosters are currently frozen
                    </Text>
                  </Box>
                </>
              )}
              {!isSettling &&
                !isRosterFrozen &&
                !isWaiverWireView &&
                ((isFreeAgentView && !isMyTeam && actions.length > 0) ||
                  (!isFreeAgentView &&
                    !isMyTeam &&
                    !isFreeAgent &&
                    !player.deal_status?.toLowerCase().includes("review") &&
                    !player.deal_status?.toLowerCase().includes("settlement") &&
                    !player.deal_status?.toLowerCase().includes("waived")) ||
                  (!isFreeAgentView &&
                    !isMyTeam &&
                    !isFreeAgent &&
                    player.deal_status?.toLowerCase().includes("waived"))) && (
                  <Text fontWeight="bold" mb={2} color="#5a5040" fontSize="sm" letterSpacing="1px" textTransform="uppercase">
                    Take Action
                  </Text>
                )}
              {isWaiverWireView &&
                !isMyTeam &&
                !(
                  player.dealData?.teams &&
                  myTeam &&
                  player.dealData.teams.some(
                    (teamId: any) => Number(teamId) === Number(myTeam)
                  )
                ) ? (
                <>
                  {player.deal_status !== 'settlement_waiver_claim' && (
                    <Flex justify="center">
                      <Button
                        size="md"
                        colorScheme="blue"
                        mt={2}
                        onClick={() => handleRosterMove("Claim Waiver")}
                      >
                        {`Claim ${playerInfo.last_name}`}
                      </Button>
                    </Flex>
                  )}
                </>
              ) : (
                <>
                  {!isRosterFrozen && isFreeAgentView && !isMyTeam && actions.length > 0 && (
                    <VStack align="stretch" spacing={2}>
                      {actions.map((action, i) => (
                        <Flex key={i} align="center">
                          {action.prohibited ? (
                            <Text fontSize="sm" color="#486e95">
                              {action.message}
                            </Text>
                          ) : (
                            <>
                              <Link
                                color="blue.700"
                                href="#"
                                mr={3}
                                fontWeight="semibold"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleRosterMove(action.label);
                                }}
                              >
                                {action.label}
                              </Link>
                              {action.note && (
                                <Text fontSize="sm" color="#8b7355">
                                  {action.note}
                                </Text>
                              )}
                            </>
                          )}
                        </Flex>
                      ))}
                    </VStack>
                  )}
                  {!isFreeAgentView &&
                    !isMyTeam &&
                    !isFreeAgent &&
                    !isDealInSettlementOrReview &&
                    isTradeAllowed() &&
                    subListLabel !== "Non-roster Players with Carried Salaries" &&
                    player.deal_status !== 'qualifying_offer_made' &&
                    !(isFranchiseTag && systemSettings?.seasonStatus && (systemSettings.seasonStatus === 'Off Season EFQO' || systemSettings.seasonStatus === 'Off Season') && !isOffSeasonQuiet) ? (
                    <VStack align="stretch" spacing={0} mt={2}>
                      <Box
                        borderLeft="3px solid"
                        borderColor="#d4cbb0"
                        borderBottom="1px solid #e0d9c8"
                        pl={3}
                        py={2}
                        _hover={{ borderColor: borderColor, bg: "#ece5d3" }}
                        _last={{ borderBottom: "none" }}
                        transition="all 0.15s"
                      >
                        <Box>
                          <Link
                            color="#333"
                            href="#"
                            fontWeight="semibold"
                            fontSize="sm"
                            _hover={{ color: borderColor }}
                            onClick={(e) => {
                              e.preventDefault();
                              handleTradeClick();
                            }}
                          >
                            {`Trade for ${playerInfo.last_name}`}
                          </Link>
                        </Box>
                      </Box>
                    </VStack>
                  ) : null}
                </>
              )}
              {/* Qualified for Offer CTA for my team */}
              {!isRosterFrozen && shouldShowOfferButton && (
                <>
                  <Divider my={4} borderColor="#d4cbb0" />
                  <Flex justify="center">
                    <Button
                      size="md"
                      colorScheme="blue"
                      mt={2}
                      isDisabled={offerButtonDisabled}
                      onClick={() => {
                        if (isPendingFA) {
                          setIsFranchisePurchaseOpen(true);
                        } else {
                          handleRosterMove("Extend Offer");
                        }
                      }}
                    >
                      {offerButtonText}
                    </Button>
                  </Flex>
                </>
              )}
              {/* {isFranchiseTagAvailable && (
              <>
                <Divider my={4} borderColor="#d4cbb0" />
                <Flex justify="center">
                  <Button
                    size="md"
                    colorScheme="green"
                    mt={2}
                    onClick={() => setIsFranchisePurchaseOpen(true)}
                    isDisabled={!franchiseTagItem}
                  >
                    {offerButtonText}
                  </Button>
                </Flex>
              </>
            )} */}

              {/* Hide 40-man options in free agent view or when deal is in settlement/review or EFQO status */}
              {!isRosterFrozen && actions.length > 0 &&
                !isSettling &&
                isMyTeam &&
                !isFreeAgent &&
                !isFreeAgentView &&
                !isDealInSettlementOrReview &&
                !player.deal_status?.toLowerCase().includes("claimed") &&
                !player.deal_status?.toLowerCase().includes("waive") &&
                player.deal_status !== "efqo" && (
                  <>
                    <Divider my={4} borderColor="#d4cbb0" />
                    <Text fontWeight="bold" mb={2} color="#5a5040" fontSize="sm" letterSpacing="1px" textTransform="uppercase">
                      Roster Moves
                    </Text>
                    <VStack align="stretch" spacing={0}>
                      {actions.map((action, i) => (
                        <Box
                          key={i}
                          borderLeft="3px solid"
                          borderColor={action.prohibited ? "#c4b89a" : "#d4cbb0"}
                          borderBottom="1px solid #e0d9c8"
                          pl={3}
                          py={2}
                          _hover={!action.prohibited ? { borderColor: borderColor, bg: "#ece5d3" } : {}}
                          _last={{ borderBottom: "none" }}
                          transition="all 0.15s"
                        >
                          {action.prohibited ? (
                            <Flex align="center" gap={2} flexWrap="wrap">
                              <Text
                                fontWeight="semibold"
                                color="#9a8e78"
                                whiteSpace="nowrap"
                                fontSize="sm"
                              >
                                {action.label}
                              </Text>
                              <Text as="em" fontSize="xs" color="#b0a58a">
                                Prohibited
                              </Text>
                              <Link
                                color="blue.700"
                                href="#"
                                fontWeight="semibold"
                                fontSize="xs"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleRosterMoveProhibited(
                                    action.label,
                                    action.message
                                  );
                                }}
                              >
                                Why?
                              </Link>
                            </Flex>
                          ) : (
                            <Box>
                              <Link
                                color="#333"
                                href="#"
                                fontWeight="semibold"
                                fontSize="sm"
                                _hover={{ color: borderColor }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleRosterMove(action.label);
                                }}
                              >
                                {action.label}
                              </Link>
                              {action.note && (
                                <Text fontSize="xs" color="#8b7355" mt="2px" lineHeight="1.3">
                                  {action.note}
                                </Text>
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </VStack>
                  </>
                )}
              {/* Hide Trade button in free agent view, waiver wire view, or when player is not on my team, waived, claimed, in settlement, is a free agent, has EFQO status, or is in Non-roster Players with Carried Salaries */}
              {(() => {
                const shouldShow =
                  !isFreeAgentView &&
                  !isWaiverWireView &&
                  isMyTeam &&
                  !isFreeAgent &&
                  !isDealInSettlementOrReview &&
                  isTradeAllowed() &&
                  player.status !== "EFQO" &&
                  subListLabel !== "Non-roster Players with Carried Salaries" &&
                  player.deal_status !== 'qualifying_offer_made' &&
                  !(isFranchiseTag && systemSettings?.seasonStatus && (systemSettings.seasonStatus === 'Off Season EFQO' || systemSettings.seasonStatus === 'Off Season') && !isOffSeasonQuiet);
                return shouldShow;
              })() ? (
                <>
                  <Divider my={4} borderColor="#d4cbb0" />
                  <Flex justify="center">
                    <Button
                      size="md"
                      colorScheme="blue"
                      mt={2}
                      onClick={handleTradeClick}
                    >
                      {`Trade ${playerInfo.last_name}`}
                    </Button>
                  </Flex>
                </>
              ) : null}
              {/* Bottom-anchored action button for free agents */}
              {!isRosterFrozen && !isSettling &&
                isFreeAgentView &&
                !isMyTeam &&
                !hasRestrictedDealStatus && (
                  <Box mt={3} mb={2} width="100%">
                    <Flex justify="center">
                      <Button
                        size="md"
                        colorScheme="blue"
                        isDisabled={
                          systemSettings?.seasonStatus === 'Off Season Quiet' ||
                          !isFreeAgentOfferWindowOpen
                        }
                        onClick={() => handleMakeOfferClick()}
                      >
                        {(() => {
                          const hasMyTeamOffer =
                            myTeam &&
                            player.dealData &&
                            player.dealData.offers &&
                            player.dealData.offers.some(
                              (offer: { team: number }) => offer.team === myTeam
                            );
                          return hasMyTeamOffer
                            ? `UPDATE OFFER`
                            : `Make offer for ${playerInfo.last_name}`;
                        })()}
                      </Button>
                    </Flex>
                  </Box>
                )}
            </Box>
            {/* ── end scrollable actions area ── */}

            {/* ── Card Footer ── */}
            <Flex
              bg={borderColor}
              px={4}
              py={1}
              borderBottomRadius="12px"
              justify="space-between"
              align="center"
              flexShrink={0}
            >
              {/* Show player_id */}
              <Text
                fontSize="9px"
                color="whiteAlpha.600"
                pointerEvents="none"
              >
                ID: {typeof player.team_id !== "undefined" &&
                  player.team_id !== null
                  ? `${player.id}-${player.team_id}-${playerInfo.id}-${player.mlb_id ?? ''}`
                  : player.id}
              </Text>

              {/* Card flip indicator icon */}
              {!isFlipped && (
                <HStack
                  spacing={2}
                  color="whiteAlpha.700"
                  cursor="pointer"
                  _hover={{ color: "white" }}
                  onClick={handleFlip}
                  transition="all 0.2s"
                  zIndex={2}
                >
                  <Text
                    fontSize="9px"
                    fontWeight="700"
                    letterSpacing="1px"
                    textTransform="uppercase"
                  >
                    STATS / TRANSACTIONS / NEWS
                  </Text>
                  <FaArrowRight
                    className={attentionActive ? "card-front-arrow-icon card-front-arrow-icon--attention" : "card-front-arrow-icon"}
                  />
                </HStack>
              )}
            </Flex>
          </Box>

          {/* Back Side — Baseball Trading Card Style */}
          <Box
            p={0}
            border={`5px solid ${innerBorderColor}`}
            borderRadius="20px"
            bg="#f5f0e1"
            width="100%"
            height="100%"
            position="absolute"
            top={0}
            left={0}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            pointerEvents={isFlipped ? "auto" : "none"}
            style={{
              backfaceVisibility: "hidden",
              WebkitBackfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
          >
            {/* Outer team-color border */}
            <Box
              width="100%"
              height="100%"
              border={`4px solid ${borderColor}`}
              borderRadius="16px"
              position="absolute"
              top={0}
              left={0}
              pointerEvents="none"
              zIndex={0}
            />

            {/* Card content wrapper — scrollable */}
            <Box
              width="100%"
              position="relative"
              zIndex={1}
              flex={1}
              overflowY="auto"
              overflowX="hidden"
              sx={{
                WebkitOverflowScrolling: "touch",
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-thumb": { bg: "rgba(0,0,0,0.15)", borderRadius: "4px" },
              }}
            >


              {/* ── Header Bar ── */}
              <Box
                bg={borderColor}
                px={5}
                pt={8}
                pb={4}
                borderTopRadius="12px"
                position="relative"
                overflow="hidden"
              >
                {onClose && (
                  <CloseButton
                    position="absolute"
                    top={2}
                    right={2}
                    zIndex={3}
                    color={bannerTextColor}
                    onClick={onClose}
                  />
                )}
                {/* Subtle diagonal stripe accent */}
                <Box
                  position="absolute"
                  top={0}
                  right={0}
                  width="160px"
                  height="100%"
                  bg={innerBorderColor}
                  opacity={0.15}
                  style={{ clipPath: "polygon(30% 0, 100% 0, 100% 100%, 0% 100%)" }}
                />
                <Text
                  fontSize="2xl"
                  fontWeight="900"
                  color={bannerTextColor}
                  letterSpacing="1px"
                  textTransform="uppercase"
                  lineHeight="1.1"
                >
                  {playerInfo.first_name} {playerInfo.last_name}
                </Text>
                <Text
                  fontSize="sm"
                  fontWeight="700"
                  color={bannerTextColor === 'white' ? (borderColor === '#333' ? 'whiteAlpha.700' : innerBorderColor) : 'rgba(43,45,66,0.6)'}
                  letterSpacing="1.5px"
                  textTransform="uppercase"
                  mt="4px"
                >
                  {positions}
                </Text>
                {isPlayerOnOtherTeam && (
                  <Text
                    fontSize="xs"
                    fontWeight="700"
                    color={bannerTextColor === 'white' ? 'whiteAlpha.700' : 'rgba(43,45,66,0.6)'}
                    letterSpacing="1px"
                    textTransform="uppercase"
                    mt="4px"
                  >
                    {otherTeamName}
                  </Text>
                )}
              </Box>

              {/* ── Vitals Strip ── */}
              <Flex
                px={5}
                py={2}
                justify="space-between"
                borderTop="1px solid #c4b89a"
                borderBottom="1px solid #c4b89a"
                flexWrap="wrap"
                gap={1}
              >
                {playerInfo.bats && (
                  <Text fontSize="xs" color="#333" fontWeight="700" letterSpacing="0.5px">
                    BATS: {playerInfo.bats}
                  </Text>
                )}
                {playerInfo.throws && (
                  <Text fontSize="xs" color="#333" fontWeight="700" letterSpacing="0.5px">
                    THROWS: {playerInfo.throws}
                  </Text>
                )}
                {playerInfo.mls !== undefined && playerInfo.mls !== null && !isNaN(playerInfo.mls) && (
                  <Text fontSize="xs" color="#333" fontWeight="700" letterSpacing="0.5px">
                    MLS: {playerInfo.mls.toFixed(3)}
                  </Text>
                )}
                {playerInfo.options !== undefined && playerInfo.options !== null && playerInfo.options !== "" && !isNaN(Number(playerInfo.options)) && (
                  <Text fontSize="xs" color="#333" fontWeight="700" letterSpacing="0.5px">
                    OPT: {playerInfo.options}
                  </Text>
                )}
                <Text fontSize="xs" color="#333" fontWeight="700" letterSpacing="0.5px">
                  SAL: {formatSalary(
                    player.playerContract?.current_salary || player.contract?.current_salary,
                    true
                  )}
                </Text>
              </Flex>

              {/* ── Dominus Stats Section (full-width table, no horizontal scroll) ── */}
              <Box px={3} pt={4} pb={2}>


                {fullStatsQuery.isFetching && (
                  <Center py={4}>
                    <Spinner size="sm" color="#8b7355" />
                  </Center>
                )}
                {fullStatsQuery.isError && (
                  <Text fontSize="sm" color="#8b7355">
                    Could not load sim stats.
                  </Text>
                )}
                {!fullStatsQuery.isFetching && !fullStatsQuery.isError && (() => {
                  const payload = fullStatsQuery.data;
                  const primaryPos = player.primaryPosition?.[0] || player.rwPlayer.position_1 || "";
                  const isPitcherPos = ["SP", "RP", "CL", "P"].includes(primaryPos.toUpperCase());

                  const allPitchRows = sortSimStatRows(getPitchingStatRows(payload));
                  const allBatRows = sortSimStatRows(getBattingStatRows(payload));

                  // Split rows by period
                  const isYtd = (r: Record<string, unknown>) => String(r.period ?? '').toLowerCase().trim() === 'ytd';
                  const is7Day = (r: Record<string, unknown>) => {
                    const p = String(r.period ?? '').toLowerCase().trim();
                    return p === '7_days' || p === '7 days' || p === 'last_7_days';
                  };

                  const pitchRowsYtd = allPitchRows.filter(isYtd);
                  const pitchRows7d = allPitchRows.filter(is7Day);
                  const batRowsYtd = allBatRows.filter(isYtd);
                  const batRows7d = allBatRows.filter(is7Day);

                  const numCell = (r: Record<string, unknown>, ...keys: string[]) => {
                    for (const k of keys) {
                      const v = r[k];
                      if (v != null && v !== "") {
                        const n = Number(v);
                        if (Number.isFinite(n)) return String(n);
                      }
                    }
                    return "—";
                  };

                  const batSinglesDisplay = (r: Record<string, unknown>) => {
                    for (const k of ["singles", "b1", "single", "h1b", "1b"]) {
                      const v = r[k];
                      if (v != null && v !== "") {
                        const n = Number(v);
                        if (Number.isFinite(n)) return String(n);
                      }
                    }
                    const h = Number(r.hits ?? r.h);
                    const d2 = Number(r.doubles ?? r["2b"] ?? r.b2 ?? 0);
                    const d3 = Number(r.triples ?? r["3b"] ?? r.b3 ?? 0);
                    const hr = Number(r.hr ?? 0);
                    if (!Number.isFinite(h)) return "—";
                    const s = h - (Number.isFinite(d2) ? d2 : 0) - (Number.isFinite(d3) ? d3 : 0) - (Number.isFinite(hr) ? hr : 0);
                    return s >= 0 ? String(s) : "—";
                  };

                  let showPitching = isPitcherPos;
                  if (showPitching && allPitchRows.length === 0 && allBatRows.length > 0) {
                    showPitching = false;
                  }
                  if (!showPitching && allBatRows.length === 0 && allPitchRows.length > 0) {
                    showPitching = true;
                  }

                  if (allPitchRows.length === 0 && allBatRows.length === 0) {
                    return (
                      <Text fontSize="sm" color="#8b7355">
                        No Dominus sim stats for this player yet.
                      </Text>
                    );
                  }

                  /* ── Shared table-building helpers ── */
                  const sectionLabelSx = {
                    fontSize: "10px",
                    fontWeight: 800,
                    color: "#5a4a2e",
                    letterSpacing: "1px",
                    textTransform: "uppercase" as const,
                    mt: 2,
                    mb: 1,
                  };

                  if (showPitching) {
                    const pitchHeaders = ["G", "W", "L", "IP", "H", "R", "ER", "BB", "SO", "SHO", "SV", "WHIP", "ERA", "WAR"];
                    const pitchCellSx = {
                      px: "1px",
                      py: "2px",
                      textAlign: "center" as const,
                      color: "#333",
                      fontSize: "8px",
                      lineHeight: 1.15,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap" as const,
                      minW: 0,
                    };
                    const pitchHeadSx = {
                      ...pitchCellSx,
                      color: "#5a5040",
                      fontWeight: 700,
                      whiteSpace: "normal" as const,
                      lineHeight: 1.05,
                      hyphens: "auto" as const,
                    };

                    const renderPitchTable = (rows: Record<string, unknown>[]) => (
                      <Box w="100%" overflow="hidden">
                        <Box
                          as="table"
                          w="100%"
                          sx={{
                            borderCollapse: "collapse",
                            tableLayout: "fixed",
                          }}
                        >
                          <Box as="thead">
                            <Box as="tr" bg="#d4cbb0">
                              {pitchHeaders.map((h) => (
                                <Box as="th" key={h} {...pitchHeadSx}>{h}</Box>
                              ))}
                            </Box>
                          </Box>
                          <Box as="tbody">
                            {rows.map((row, idx) => (
                              <Box
                                as="tr"
                                key={String(row.id ?? `${rowSeasonLabel(row)}-${String(row.period)}-${idx}`)}
                                bg={idx % 2 === 0 ? "transparent" : "#ece5d3"}
                              >
                                <Box as="td" {...pitchCellSx}>{numCell(row, "g")}</Box>
                                <Box as="td" {...pitchCellSx}>{numCell(row, "w")}</Box>
                                <Box as="td" {...pitchCellSx}>{numCell(row, "l")}</Box>
                                <Box as="td" {...pitchCellSx}>{formatIpDisplay(row.ip)}</Box>
                                <Box as="td" {...pitchCellSx}>{numCell(row, "h")}</Box>
                                <Box as="td" {...pitchCellSx}>{numCell(row, "r")}</Box>
                                <Box as="td" {...pitchCellSx}>{numCell(row, "er")}</Box>
                                <Box as="td" {...pitchCellSx}>{numCell(row, "bb")}</Box>
                                <Box as="td" {...pitchCellSx}>{numCell(row, "so")}</Box>
                                <Box as="td" {...pitchCellSx}>{numCell(row, "sho", "shutouts", "cg_sho", "cg_shutouts")}</Box>
                                <Box as="td" {...pitchCellSx}>{numCell(row, "sv")}</Box>
                                <Box as="td" {...pitchCellSx}>{formatDecimalFixed(row.whip, 3)}</Box>
                                <Box as="td" {...pitchCellSx}>{formatDecimalFixed(row.era, 3)}</Box>
                                <Box as="td" {...pitchCellSx}>{formatDecimalFixed(row.war, 3)}</Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    );

                    return (
                      <VStack w="100%" spacing={0} align="stretch">
                        <Text
                          fontSize="md"
                          fontWeight="900"
                          color="#5a4a2e"
                          letterSpacing="1.5px"
                          textTransform="uppercase"
                          mb={3}
                        >
                          Dominus Pitching Stats
                        </Text>
                        {pitchRowsYtd.length > 0 && (
                          <>
                            <Text {...sectionLabelSx}>{CURRENT_YEAR} Season</Text>
                            {renderPitchTable(pitchRowsYtd)}
                          </>
                        )}
                        {pitchRows7d.length > 0 && (
                          <>
                            <Text {...sectionLabelSx} mt={pitchRowsYtd.length > 0 ? 3 : 2}>Last 7 Days</Text>
                            {renderPitchTable(pitchRows7d)}
                          </>
                        )}
                      </VStack>
                    );
                  }

                  const batHeaders = ["G", "AB", "SB", "R", "H", "1B", "2B", "3B", "HR", "RBI", "BA"];
                  const batCellSx = {
                    px: "2px",
                    py: "2px",
                    textAlign: "center" as const,
                    color: "#333",
                    fontSize: "9px",
                    lineHeight: 1.15,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap" as const,
                    minW: 0,
                  };
                  const batHeadSx = {
                    ...batCellSx,
                    color: "#5a5040",
                    fontWeight: 700,
                    whiteSpace: "normal" as const,
                    lineHeight: 1.05,
                  };

                  const renderBatTable = (rows: Record<string, unknown>[]) => (
                    <Box w="100%" overflow="hidden">
                      <Box
                        as="table"
                        w="100%"
                        sx={{
                          borderCollapse: "collapse",
                          tableLayout: "fixed",
                        }}
                      >
                        <Box as="thead">
                          <Box as="tr" bg="#d4cbb0">
                            {batHeaders.map((h) => (
                              <Box as="th" key={h} {...batHeadSx}>{h}</Box>
                            ))}
                          </Box>
                        </Box>
                        <Box as="tbody">
                          {rows.map((row, idx) => {
                            const hits = row.hits ?? row.h;
                            const hitsStr =
                              hits != null && hits !== "" && Number.isFinite(Number(hits))
                                ? String(hits)
                                : "—";
                            return (
                              <Box
                                as="tr"
                                key={String(row.id ?? `${rowSeasonLabel(row)}-${String(row.period)}-${idx}`)}
                                bg={idx % 2 === 0 ? "transparent" : "#ece5d3"}
                              >
                                <Box as="td" {...batCellSx}>{numCell(row, "g", "games")}</Box>
                                <Box as="td" {...batCellSx}>{numCell(row, "ab", "at_bats")}</Box>
                                <Box as="td" {...batCellSx}>{numCell(row, "sb")}</Box>
                                <Box as="td" {...batCellSx}>{numCell(row, "runs", "r")}</Box>
                                <Box as="td" {...batCellSx}>{hitsStr}</Box>
                                <Box as="td" {...batCellSx}>{batSinglesDisplay(row)}</Box>
                                <Box as="td" {...batCellSx}>{numCell(row, "doubles", "b2", "two_b", "2b")}</Box>
                                <Box as="td" {...batCellSx}>{numCell(row, "triples", "b3", "three_b", "3b")}</Box>
                                <Box as="td" {...batCellSx}>{numCell(row, "hr", "home_runs")}</Box>
                                <Box as="td" {...batCellSx}>{numCell(row, "rbi", "rbis")}</Box>
                                <Box as="td" {...batCellSx}>{formatAvg3(row.bavg ?? row.avg)}</Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  );

                  return (
                    <VStack w="100%" spacing={0} align="stretch">
                      <Text
                        fontSize="md"
                        fontWeight="900"
                        color="#5a4a2e"
                        letterSpacing="1.5px"
                        textTransform="uppercase"
                        mb={3}
                      >
                        Dominus Batting Stats
                      </Text>
                      {batRowsYtd.length > 0 && (
                        <>
                          <Text {...sectionLabelSx}>{CURRENT_YEAR} Season</Text>
                          {renderBatTable(batRowsYtd)}
                        </>
                      )}
                      {batRows7d.length > 0 && (
                        <>
                          <Text {...sectionLabelSx} mt={batRowsYtd.length > 0 ? 3 : 2}>Last 7 Days</Text>
                          {renderBatTable(batRows7d)}
                        </>
                      )}
                    </VStack>
                  );
                })()}
              </Box>

              {/* ── Transaction History — collapsible + scroll inside card (less whole-card scroll) ── */}
              <Box px={5} pt={4} pb={2}>
                <Accordion allowToggle defaultIndex={[0]}>
                  <AccordionItem
                    border="1px solid #c4b89a"
                    borderRadius="md"
                    overflow="hidden"
                    bg="rgba(255,255,255,0.35)"
                  >
                    <AccordionButton
                      px={3}
                      py={2.5}
                      _expanded={{ bg: "#ece5d3" }}
                      _hover={{ bg: "#ebe6d5" }}
                    >
                      <Box
                        as="span"
                        flex="1"
                        textAlign="left"
                        fontSize="md"
                        fontWeight="900"
                        color="#5a4a2e"
                        letterSpacing="1.5px"
                        textTransform="uppercase"
                      >
                        Transaction History
                      </Box>
                      <AccordionIcon color="#5a4a2e" />
                    </AccordionButton>
                    <AccordionPanel px={0} pt={0} pb={2}>
                      <Box
                        maxH="168px"
                        overflowY="auto"
                        overflowX="hidden"
                        borderTop="1px solid #d4cbb0"
                        px={2}
                        py={2}
                        mx={2}
                        mb={1}
                        borderRadius="sm"
                        sx={{
                          WebkitOverflowScrolling: "touch",
                          "&::-webkit-scrollbar": { width: "4px" },
                          "&::-webkit-scrollbar-thumb": {
                            bg: "rgba(0,0,0,0.2)",
                            borderRadius: "4px",
                          },
                        }}
                      >
                        <RecentDominusActivity
                          rosterPlayerId={player.id}
                          leagueId={myLeague?.id}
                          myTeam={myTeam ?? undefined}
                          maxItems={12}
                          enabled={isFlipped}
                          embedded
                        />
                      </Box>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              </Box>

              {/* ── External Links ── */}
              {(() => {
                const mlbId = (rwP as any).mlb_id;
                const mlbIdStr = mlbId ? String(mlbId).trim() : "";
                const isValidMlbId = mlbIdStr && /^\d+$/.test(mlbIdStr);
                if (!isValidMlbId) return null;

                return (
                  <Flex
                    px={5}
                    pt={3}
                    pb={2}
                    gap={5}
                    borderTop="1px solid #c4b89a"
                    mt={2}
                    letterSpacing="1.5px"
                    textTransform="uppercase"
                  >
                    <Link
                      href={`https://baseballsavant.mlb.com/savant-player/${mlbIdStr}`}
                      isExternal
                      fontSize="xs"
                      color="#5a4a2e"
                      fontWeight="700"
                      _hover={{ color: borderColor, textDecoration: "underline" }}
                    >
                      Baseball Savant ↗
                    </Link>
                    <Link
                      href={`https://www.mlb.com/player/${mlbIdStr}`}
                      isExternal
                      fontSize="xs"
                      color="#5a4a2e"
                      fontWeight="700"
                      _hover={{ color: borderColor, textDecoration: "underline" }}
                    >
                      MLB Player Page ↗
                    </Link>
                  </Flex>
                );
              })()}

              {/* ── Footer branding ── */}
              <Center pt={3} pb={4}>
                <Text
                  fontSize="10px"
                  color={borderColor}
                  letterSpacing="4px"
                  textTransform="uppercase"
                  fontWeight="700"
                  opacity={0.4}
                >
                  Dominus Baseball
                </Text>
              </Center>
            </Box>

            {/* Flip icon on back side */}
            {isFlipped && (
              <Box
                position="absolute"
                bottom="12px"
                left="12px"
                color="#8b7355"
                opacity={0.7}
                fontSize="14px"
                cursor="pointer"
                _hover={{ opacity: 1, color: "#5a5040" }}
                onClick={handleFlip}
                transition="all 0.2s"
                zIndex={3}
              >
                <FaArrowLeft />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Confirmation Dialog for Roster Moves */}
      {showConfirmation &&
        confirmationMessages.length > 0 &&
        transactionData && (
          <ConfirmDialog
            isOpen={showConfirmation}
            onClose={() => setShowConfirmation(false)}
            onConfirm={handleConfirmationResponse}
            title={rosterMoveName(selectedMove, playerInfo)}
            messages={confirmationMessages}
            type={invalidMove ? "warning" : "confirmation"}
            button1={confirmationButtons[0]}
            button2={confirmationButtons[1]}
          />
        )}
      {franchiseTagItem && (
        <QuickPurchaseModal
          isOpen={isFranchisePurchaseOpen}
          onClose={() => setIsFranchisePurchaseOpen(false)}
          item={franchiseTagItem}
          onPaymentIntentCreated={handleFranchiseTagPurchaseCreated}
        />
      )}
      {/* IL Replacement Modal */}
      {showILReplacementModal && (
        <ILReplacementModal
          isOpen={showILReplacementModal}
          onClose={() => setShowILReplacementModal(false)}
          ilPlayer={player}
          teamId={myTeam || 0}
          fortyManRosterSize={fortyManRosterSize ?? null}
          onConfirm={handleILReplacementConfirm}
          isProcessing={isILProcessing}
        />
      )}
    </>
  );
};

export default PlayerCard; 